import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import './CourseManagementPopover.css';

function CourseManagementPopover({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [form, setForm] = useState({
    dreamapply: '',
    neptun: '',
    module_code: '',
    education_level: '',
    coordinator: '',
    coordinator_code: '',
    curriculum_code: '',
    tuition_fee: '',
  });

  const resetForm = () => {
    setForm({
      dreamapply: '',
      neptun: '',
      module_code: '',
      education_level: '',
      coordinator: '',
      coordinator_code: '',
      curriculum_code: '',
    });
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/admin/courses');
      setItems(response.data || []);
    } catch (err) {
      console.error('Failed to load course mappings:', err);
      setError(err.response?.data?.detail || 'Képzések betöltése sikertelen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const payload = {
        dreamapply: form.dreamapply.trim(),
        neptun: form.neptun.trim(),
        module_code: form.module_code.trim() || null,
        education_level: form.education_level.trim() || null,
        coordinator: form.coordinator.trim() || null,
        coordinator_code: form.coordinator_code.trim() || null,
        curriculum_code: form.curriculum_code.trim() || null,
        tuition_fee: form.tuition_fee.trim() || null,
      };
      if (!payload.dreamapply || !payload.neptun) {
        setError('A DreamApply és Neptun mezők kitöltése kötelező.');
        setSaving(false);
        return;
      }
      const response = await api.post('/api/admin/courses', payload);
      const saved = response.data;
      setItems((prev) => {
        const others = prev.filter((i) => i.dreamapply !== saved.dreamapply);
        return [...others, saved].sort((a, b) => a.dreamapply.localeCompare(b.dreamapply));
      });
      setForm({
        dreamapply: '',
        neptun: '',
        module_code: '',
        education_level: '',
        coordinator: '',
        coordinator_code: '',
        curriculum_code: '',
      });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to save course mapping:', err);
      setError(err.response?.data?.detail || 'Mentés sikertelen');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      dreamapply: item.dreamapply || '',
      neptun: item.neptun || '',
      module_code: item.module_code || '',
      education_level: item.education_level || '',
      coordinator: item.coordinator || '',
      coordinator_code: item.coordinator_code || '',
      curriculum_code: item.curriculum_code || '',
      tuition_fee: item.tuition_fee || '',
    });
    setShowAddForm(true);
  };

  const handleDelete = async (dreamapply) => {
    if (!window.confirm('Biztosan törölni szeretné ezt a képzés-mappolást?')) return;
    try {
      setDeletingKey(dreamapply);
      const encoded = encodeURIComponent(dreamapply);
      await api.delete(`/api/admin/courses/${encoded}`);
      setItems((prev) => prev.filter((i) => i.dreamapply !== dreamapply));
    } catch (err) {
      console.error('Failed to delete course mapping:', err);
      alert(err.response?.data?.detail || 'Törlés sikertelen');
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <div className="course-mgmt-overlay">
      <div className="course-mgmt-popover">
        <div className="course-mgmt-header">
          <h2>Képzések</h2>
          <button className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {error && <div className="course-mgmt-error">{error}</div>}

        <div className="course-mgmt-toolbar">
          <button
            className="add-item-button"
            type="button"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
          >
            <Plus size={16} />
            Új képzés
          </button>
        </div>

        {showAddForm && (
          <div className="course-mgmt-add-form-wrapper">
            <form className="course-mgmt-add-form" onSubmit={handleSave}>
              <div className="form-row">
                <label>DreamApply képzés neve (pl. "[BSc] Business and Management")</label>
                <input
                  type="text"
                  name="dreamapply"
                  required
                  value={form.dreamapply}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Neptun képzés neve (magyar)</label>
                <input
                  type="text"
                  name="neptun"
                  required
                  value={form.neptun}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Modul kód</label>
                <input
                  type="text"
                  name="module_code"
                  value={form.module_code}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Képzési szint</label>
                <input
                  type="text"
                  name="education_level"
                  value={form.education_level}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Koordinátor neve</label>
                <input
                  type="text"
                  name="coordinator"
                  value={form.coordinator}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Koordinátor kódja</label>
                <input
                  type="text"
                  name="coordinator_code"
                  value={form.coordinator_code}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Mintatanterv kódja</label>
                <input
                  type="text"
                  name="curriculum_code"
                  value={form.curriculum_code}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Listaár (tuition fee)</label>
                <input
                  type="text"
                  name="tuition_fee"
                  value={form.tuition_fee}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="course-mgmt-form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowAddForm(false)}
                  disabled={saving}
                >
                  Mégse
                </button>
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Mentés...' : 'Mentés'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="course-mgmt-list">
          {loading ? (
            <div className="course-mgmt-loading">Betöltés...</div>
          ) : items.length === 0 ? (
            <div className="course-mgmt-empty">Még nincsenek képzés-mappolások.</div>
          ) : (
            items
              .slice()
              .sort((a, b) => a.dreamapply.localeCompare(b.dreamapply))
              .map((item) => (
                <div key={item.dreamapply} className="course-mgmt-item">
                  <div className="course-mgmt-content" onDoubleClick={() => handleEdit(item)}>
                    <div className="course-mgmt-title">
                      <span className="course-mgmt-dreamapply">{item.dreamapply}</span>
                      <span className="course-mgmt-sep"> - </span>
                      <span className="course-mgmt-neptun">{item.neptun}</span>
                    </div>
                    <div className="course-mgmt-meta-grid">
                      <div className="course-mgmt-meta-cell">
                        <span className="course-mgmt-meta-label">Szint:</span>
                        <span className="course-mgmt-meta-value">{item.education_level || '-'}</span>
                      </div>
                      <div className="course-mgmt-meta-cell">
                        <span className="course-mgmt-meta-label">Modul:</span>
                        <span className="course-mgmt-meta-value">{item.module_code || '-'}</span>
                      </div>
                      <div className="course-mgmt-meta-cell">
                        <span className="course-mgmt-meta-label">Koordinátor neve:</span>
                        <span className="course-mgmt-meta-value">{item.coordinator || '-'}</span>
                      </div>
                      <div className="course-mgmt-meta-cell">
                        <span className="course-mgmt-meta-label">Kódja:</span>
                        <span className="course-mgmt-meta-value">{item.coordinator_code || '-'}</span>
                      </div>
                      <div className="course-mgmt-meta-cell">
                        <span className="course-mgmt-meta-label">Mintatanterv kódja:</span>
                        <span className="course-mgmt-meta-value">{item.curriculum_code || '-'}</span>
                      </div>
                      <div className="course-mgmt-meta-cell">
                        <span className="course-mgmt-meta-label">Listaár:</span>
                        <span className="course-mgmt-meta-value">{item.tuition_fee || '-'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="course-mgmt-actions">
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => handleEdit(item)}
                    >
                      Szerkesztés
                    </button>
                    <button
                      type="button"
                      className="icon-button danger"
                      onClick={() => handleDelete(item.dreamapply)}
                      disabled={deletingKey === item.dreamapply}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

export default CourseManagementPopover;
